import React, { useState } from 'react';
import { DetailedScore } from '../services/scoringService';
import { cn } from '../lib/cn';
import { useT } from '../lib/i18n';

interface ScoreBreakdownProps {
  breakdown: DetailedScore;
  compact?: boolean;
}

const ScoreDot: React.FC<{ className?: string }> = ({ className }) => (
  <span className={cn('inline-block w-2.5 h-2.5 rounded-full', className)} aria-hidden="true" />
);

const SCORE_STYLE = {
  freshness: {
    icon: <ScoreDot className="bg-purple-400" />,
    color: 'text-purple-400',
    bgColor: 'bg-purple-400/10',
    borderColor: 'border-purple-400/30',
  },
  specificity: {
    icon: <ScoreDot className="bg-blue-400" />,
    color: 'text-blue-400',
    bgColor: 'bg-blue-400/10',
    borderColor: 'border-blue-400/30',
  },
  personaFit: {
    icon: <ScoreDot className="bg-green-400" />,
    color: 'text-green-400',
    bgColor: 'bg-green-400/10',
    borderColor: 'border-green-400/30',
  },
  replyHook: {
    icon: <ScoreDot className="bg-yellow-400" />,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-400/10',
    borderColor: 'border-yellow-400/30',
  },
  readability: {
    icon: <ScoreDot className="bg-pink-400" />,
    color: 'text-pink-400',
    bgColor: 'bg-pink-400/10',
    borderColor: 'border-pink-400/30',
  },
  risk: {
    icon: <ScoreDot className="bg-red-400" />,
    color: 'text-red-400',
    bgColor: 'bg-red-400/10',
    borderColor: 'border-red-400/30',
  },
};

const SCORE_KEYS = ['freshness', 'specificity', 'personaFit', 'replyHook', 'readability', 'risk'] as const;
type ScoreKey = typeof SCORE_KEYS[number];

export const ScoreBreakdown: React.FC<ScoreBreakdownProps> = ({ breakdown, compact = false }) => {
  const [hoveredKey, setHoveredKey] = useState<keyof DetailedScore | null>(null);
  const { t } = useT();

  const getScoreInfo = (key: ScoreKey) => {
    const style = SCORE_STYLE[key];
    return {
      ...style,
      label: t(`scoreBreakdown.${key}.label`),
      description: t(`scoreBreakdown.${key}.description`),
      tips: [
        t(`scoreBreakdown.${key}.tips.0`),
        t(`scoreBreakdown.${key}.tips.1`),
        t(`scoreBreakdown.${key}.tips.2`),
      ],
    };
  };

  if (compact) {
    return (
      <div className="flex flex-wrap gap-2 text-xs">
        {(Object.entries(breakdown) as [keyof DetailedScore, number][])
          .filter(([key]) => key !== 'total')
          .map(([key, value]) => {
            if (!SCORE_KEYS.includes(key as ScoreKey)) return null;
            const info = getScoreInfo(key as ScoreKey);

            return (
              <div
                key={key}
                className="relative group"
                onMouseEnter={() => setHoveredKey(key)}
                onMouseLeave={() => setHoveredKey(null)}
              >
                <span className="flex items-center gap-1 text-text-muted">
                  <span className="opacity-50">{info.icon}</span>
                  {info.label}: {(Math.abs(value) * 100).toFixed(0)}%
                </span>

                {/* Tooltip */}
                {hoveredKey === key && (
                  <div className="absolute z-10 bottom-full left-0 mb-2 w-64 p-3 bg-surface-2 border border-border rounded-lg shadow-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={cn('text-lg', info.color)}>{info.icon}</span>
                      <p className="font-medium text-text-primary">{info.label}</p>
                    </div>
                    <p className="text-xs text-text-muted mb-2">{info.description}</p>
                    <div className="space-y-1">
                      {info.tips.map((tip: string, i: number) => (
                        <p key={i} className="text-xs text-text-muted">
                          • {tip}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {(Object.entries(breakdown) as [keyof DetailedScore, number][])
        .filter(([key]) => key !== 'total')
        .map(([key, value]) => {
          if (!SCORE_KEYS.includes(key as ScoreKey)) return null;
          const info = getScoreInfo(key as ScoreKey);

          const absValue = Math.abs(value);
          const percentage = (absValue * 100).toFixed(0);
          const isRisk = key === 'risk';

          return (
            <div key={key} className="space-y-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={cn('text-base', info.color)}>{info.icon}</span>
                  <p className="text-sm font-medium text-text-primary">{info.label}</p>
                </div>
                <span className={cn('text-sm font-bold tabular-nums', info.color)}>
                  {isRisk && value > 0 && '-'}{percentage}%
                </span>
              </div>

              {/* Progress Bar */}
              <div className="relative h-2 bg-surface-1 rounded-full overflow-hidden">
                <div
                  className={cn(
                    'h-full transition-all duration-300',
                    info.bgColor.replace('/10', '')
                  )}
                  style={{ width: `${absValue * 100}%` }}
                />
              </div>

              {/* Description */}
              <p className="text-xs text-text-muted">{info.description}</p>

              {/* Tips (Expandable) */}
              <details className="text-xs">
                <summary className="cursor-pointer text-brand hover:underline">
                  {t('scoreBreakdown.improvementHints')}
                </summary>
                <ul className="mt-1 space-y-1 text-text-muted pl-4">
                  {info.tips.map((tip: string, i: number) => (
                    <li key={i}>• {tip}</li>
                  ))}
                </ul>
              </details>
            </div>
          );
        })}

      {/* Total Score */}
      <div className={cn(
        'mt-4 p-3 rounded-lg border-2',
        breakdown.total >= 0.8
          ? 'bg-success/10 border-success text-success'
          : breakdown.total >= 0.6
            ? 'bg-brand/10 border-brand text-brand'
            : 'bg-text-muted/10 border-text-muted text-text-muted'
      )}>
        <div className="flex items-center justify-between">
          <p className="font-bold">{t('scoreBreakdown.totalScore')}</p>
          <p className="text-2xl font-bold">{(breakdown.total * 100).toFixed(0)}</p>
        </div>
        <p className="text-xs opacity-75 mt-1">
          {breakdown.total >= 0.8
            ? t('scoreBreakdown.feedback.excellent')
            : breakdown.total >= 0.6
              ? t('scoreBreakdown.feedback.good')
              : t('scoreBreakdown.feedback.needsWork')}
        </p>
      </div>
    </div>
  );
};
